import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
	type CreatePropertyBody,
	PROPERTY_LIMITS,
	PROPERTY_TYPES,
	type PropertyResponse,
	type PropertyType,
} from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import type React from "react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ApiError, apiFetch } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { PageContainer } from "../components/page-container"
import { useLastPropertyId } from "../hooks/use-last-property-id"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

function fillTemplate(
	template: string,
	vars: Record<string, string | number>,
): string {
	return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ""))
}

export function PropertyNewPage() {
	const navigate = useNavigate()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const { setLastPropertyId } = useLastPropertyId()
	const queryClient = useQueryClient()

	const createProperty = useMutation({
		mutationFn: (body: CreatePropertyBody) =>
			apiFetch<PropertyResponse>("/api/common/properties", {
				method: "POST",
				body: JSON.stringify(body),
			}),
		onSuccess: async (res) => {
			await queryClient.invalidateQueries({ queryKey: queryKeys.properties() })
			setLastPropertyId(res.property.id)
			navigate(webPaths.property(res.property.id))
		},
	})

	const [name, setName] = useState("")
	const [type, setType] = useState<PropertyType | "">("")
	const [customType, setCustomType] = useState("")
	const [description, setDescription] = useState("")
	const [errors, setErrors] = useState<{
		name?: string
		type?: string
		customType?: string
		description?: string
		form?: string
	}>({})

	function validate(): boolean {
		const next: typeof errors = {}
		const trimmedName = name.trim()
		if (trimmedName.length === 0) {
			next.name = t.properties.form.errors.nameRequired
		} else if (trimmedName.length > PROPERTY_LIMITS.nameMax) {
			next.name = fillTemplate(t.properties.form.errors.nameTooLong, {
				max: PROPERTY_LIMITS.nameMax,
			})
		}

		if (!type) next.type = t.properties.form.errors.typeRequired

		if (type === "other") {
			const trimmedCustom = customType.trim()
			if (trimmedCustom.length === 0) {
				next.customType = t.properties.form.errors.customTypeRequired
			} else if (trimmedCustom.length > PROPERTY_LIMITS.customTypeMax) {
				next.customType = fillTemplate(
					t.properties.form.errors.customTypeTooLong,
					{ max: PROPERTY_LIMITS.customTypeMax },
				)
			}
		}

		if (description.trim().length > PROPERTY_LIMITS.descriptionMax) {
			next.description = fillTemplate(
				t.properties.form.errors.descriptionTooLong,
				{ max: PROPERTY_LIMITS.descriptionMax },
			)
		}

		setErrors(next)
		return Object.keys(next).length === 0
	}

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault()
		if (!validate()) return
		setErrors((prev) => ({ ...prev, form: undefined }))
		const trimmedDescription = description.trim()
		const body: CreatePropertyBody = {
			name: name.trim(),
			type: type as PropertyType,
			...(type === "other" ? { customType: customType.trim() } : {}),
			...(trimmedDescription.length > 0
				? { description: trimmedDescription }
				: {}),
		}
		try {
			await createProperty.mutateAsync(body)
		} catch (err) {
			const message =
				err instanceof ApiError ? err.message : t.properties.form.errors.unknown
			setErrors((prev) => ({ ...prev, form: message }))
		}
	}

	return (
		<div className="min-h-screen w-full bg-background flex flex-col">
			<PageContainer variant="narrow" chrome="bare">
				<h1 className="text-4xl font-black text-foreground tracking-tight mb-8">
					{t.properties.form.newPageTitle}
				</h1>

				<form onSubmit={onSubmit} className="space-y-6" noValidate>
					<div>
						<label
							htmlFor="name"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.properties.form.nameLabel}
						</label>
						<input
							id="name"
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t.properties.form.namePlaceholder}
							className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
							maxLength={PROPERTY_LIMITS.nameMax + 10}
							aria-invalid={Boolean(errors.name)}
							aria-describedby={errors.name ? "name-error" : undefined}
						/>
						{errors.name ? (
							<p id="name-error" className="text-sm text-destructive mt-1">
								{errors.name}
							</p>
						) : null}
					</div>

					<fieldset aria-describedby={errors.type ? "type-error" : undefined}>
						<legend className="block text-sm font-semibold text-foreground/80 mb-2">
							{t.properties.form.typeLabel}
						</legend>
						<div className="grid grid-cols-2 gap-2">
							{PROPERTY_TYPES.map((pt) => (
								<label
									key={pt}
									className={
										"flex items-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-colors " +
										(type === pt
											? "border-ring bg-accent text-foreground"
											: "border-border bg-muted/40 text-foreground/80 hover:border-foreground/25")
									}
								>
									<input
										type="radio"
										name="type"
										value={pt}
										checked={type === pt}
										onChange={() => setType(pt)}
										className="accent-ring"
									/>
									<span>{t.properties.types[pt]}</span>
								</label>
							))}
						</div>
						{errors.type ? (
							<p id="type-error" className="text-sm text-destructive mt-2">
								{errors.type}
							</p>
						) : null}
					</fieldset>

					{type === "other" ? (
						<div>
							<label
								htmlFor="customType"
								className="block text-sm font-semibold text-foreground/80 mb-2"
							>
								{t.properties.form.customTypeLabel}
							</label>
							<input
								id="customType"
								type="text"
								value={customType}
								onChange={(e) => setCustomType(e.target.value)}
								placeholder={t.properties.form.customTypePlaceholder}
								className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
								maxLength={PROPERTY_LIMITS.customTypeMax + 10}
								aria-invalid={Boolean(errors.customType)}
								aria-describedby={
									errors.customType ? "customType-error" : undefined
								}
							/>
							{errors.customType ? (
								<p
									id="customType-error"
									className="text-sm text-destructive mt-1"
								>
									{errors.customType}
								</p>
							) : null}
						</div>
					) : null}

					<div>
						<label
							htmlFor="description"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.properties.form.descriptionLabel}
						</label>
						<textarea
							id="description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder={t.properties.form.descriptionPlaceholder}
							rows={5}
							className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none resize-y"
							maxLength={PROPERTY_LIMITS.descriptionMax + 100}
							aria-invalid={Boolean(errors.description)}
							aria-describedby={
								errors.description ? "description-error" : undefined
							}
						/>
						{errors.description ? (
							<p
								id="description-error"
								className="text-sm text-destructive mt-1"
							>
								{errors.description}
							</p>
						) : null}
					</div>

					{errors.form ? (
						<div
							role="alert"
							className="rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
						>
							{errors.form}
						</div>
					) : null}

					<div className="flex items-center gap-3 pt-2">
						<Button
							type="submit"
							variant="brand"
							size="lg"
							disabled={createProperty.isPending}
						>
							{t.properties.form.submit}
						</Button>
						<Button
							type="button"
							variant="ghost"
							size="lg"
							onClick={() => navigate(webPaths.dashboard())}
							disabled={createProperty.isPending}
						>
							{t.properties.form.cancel}
						</Button>
					</div>
				</form>
			</PageContainer>
		</div>
	)
}
